import * as React from 'react'
import { closeNewsTagModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import ModalLoader from './Parts/ModalLoader'
import Button from '../Button/Button'
import { CurrentUser, DisplayableError, NewsTag } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import { NewsTagsController } from '../../controllers'

interface IStateToProps {
  currentUser: CurrentUser
  newsCenterId: string
  newsTag?: NewsTag
  onSubmit: (newsTag: NewsTag) => void
  onDelete: (newsTag: NewsTag) => void
}

interface IDispatchToProps {
  close: typeof closeNewsTagModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  newsTag: NewsTag
  errors: DisplayableError[]
}

const NewsTagModal = (props: IProps) => {
  const { t } = useTranslation()
  const { currentUser, newsCenterId, newsTag: propsNewsTag } = props
  const { workspace: { setting } } = currentUser
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    newsTag: null,
    errors: [],
  })
  const { didInitialLoad, newsTag, errors } = state

  React.useEffect(() => {
    getForm().catch(console.error)
  }, [])

  const getForm = async () => {
    try {
      const { news_tag } = await NewsTagsController.getForm(newsCenterId, propsNewsTag.id)

      setState({
        ...state,
        newsTag: {
          ...news_tag,
          ...propsNewsTag,
        },
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const onNameChange = (e) => {
    const newName = e.currentTarget.value

    setState({ ...state, newsTag: { ...newsTag, name: newName } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onDeleteClick = async () => {
    try {
      await NewsTagsController.delete(newsCenterId, newsTag.id)
      props.onDelete(newsTag)
      onCloseClick()
    } catch (ex) {
      console.error(ex)
    }
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    try {
      let response = null
      if (newsTag.id) {
        response = await NewsTagsController.update(newsCenterId, newsTag)
      } else {
        response = await NewsTagsController.create(newsCenterId, newsTag)
      }

      if (response.errors) {
        setState({ ...state, errors: response.errors })
      } else {
        props.onSubmit(response)
        props.close()
      }

    } catch (ex) {
      console.error(ex)
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={newsTag?.id ? t('NewsTagModal::Update tag') : t('NewsTagModal::Create tag')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-12col'>
                <div className='form-item'>
                  <label>{t('NewsTagModal::Name')} <span>*</span></label>
                  <input
                    type='text'
                    placeholder={t('NewsTagModal::Name')}
                    onChange={onNameChange}
                    value={newsTag.name}
                  />
                </div>
              </div>
            </div>

            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div>
            {newsTag?.id && <Button
              type='danger'
              text={t('NewsTagModal::Delete')}
              onClick={onDeleteClick}
            />}
          </div>
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={t('NewsTagModal::Save')}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow >
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser
    },
    modals: {
      newsTagModal: {
        newsCenterId,
        newsTag,
        onSubmit,
        onDelete,
      }
    }
  } = state

  return {
    currentUser: currentUser,
    newsCenterId: newsCenterId,
    newsTag: newsTag,
    onSubmit: onSubmit,
    onDelete: onDelete
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeNewsTagModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(NewsTagModal)